import { SortDescriptor } from "@zapier/design-system";
import {
  AuthenticationsOrder,
  AuthenticationsStatus,
  OwnerStatus,
} from "@zapier/bff";
import { useConnections } from "@zapier/client";
import { parseMultiParameter, useRouterParams } from "@zapier/shared";
import { CONNECTION_STATUS_NON_COMPLIANT } from "@zapier/utils";
import { CONNECTION_STATUS_DISABLED } from "./constants";
import { useEffect, useState } from "react";

interface Input {
  pageSize: number;
}

export function useConnectionsList({ pageSize }: Input) {
  // read query params from URL when component mounts
  const {
    app,
    apps: appsRaw,
    appsOperator: appsOperatorRaw,
    managedAuthViolation,
    ownerStatus,
    page,
    order,
    search,
    owner: selectedOwner,
    owners: ownersRaw,
    ownersOperator: ownersOperatorRaw,
    shared: selectedShared,
    status,
    setParams,
  } = useRouterParams();

  /** App filtering ****************************************** */

  // Extract the versionless selected API from the app string,
  // Since the apps in the dropdown filter do not appear versioned to the user

  const selectedApi = (app || "").split("@")[0].split("::")[0];

  const parsedApps = parseMultiParameter(appsRaw, appsOperatorRaw);
  const apps =
    parsedApps.values.length > 0 ? parsedApps.values.join(",") : undefined;
  const appsOperator = apps ? parsedApps.operator : undefined;

  /** Owner selection **************************************** */

  let owner = selectedOwner;
  let shared;
  let sharedByMe;

  // "Shared with me", allow owner filtering
  if (selectedShared === "-me") {
    shared = true;
  }

  // 'Owned by me"
  if (selectedShared === "me") {
    owner = "me";
  }

  // 'Shared by me'
  if (selectedShared === "shared-by-me") {
    sharedByMe = true;
  }

  // 'Private', allow owner filtering
  if (selectedShared === "private") {
    shared = false;
  }

  // 'Everyone', reset shared
  if (selectedShared === "everyone") {
    shared = undefined;
  }

  const parsedOwners = parseMultiParameter(ownersRaw, ownersOperatorRaw);
  const owners =
    parsedOwners.values.length > 0 ? parsedOwners.values.join(",") : undefined;
  const ownersOperator = owners ? parsedOwners.operator : undefined;

  /** Sorting *********************************************** */

  const sortChange = (descriptor: SortDescriptor) => {
    const orderChange = `${descriptor.direction === "ascending" ? "" : "-"}${descriptor.column}`;
    if (orderChange !== order) {
      setParams({ order: orderChange, page: 1 });
    }
  };

  /** Fetch connections *********************************************** */

  // Disabled is exclusive of Active and Expired, matching the row status precedence.
  let isRestricted: boolean | undefined;
  if (status === CONNECTION_STATUS_DISABLED) {
    isRestricted = true;
  } else if (status === "active" || status === "expired") {
    isRestricted = false;
  }

  const {
    isFetching,
    isLoading,
    isLoadingSecondary,
    totalPages,
    totalCount,
    connections,
  } = useConnections({
    order: order ? (order as AuthenticationsOrder) : undefined,
    owner: owner || "",
    owners,
    ownersOperator,
    ownerStatus: ownerStatus ? (ownerStatus as OwnerStatus) : undefined,
    page,
    pageSize,
    search: search || "",
    selectedApi,
    apps,
    appsOperator,
    shared,
    sharedByMe,
    status:
      status &&
      status !== CONNECTION_STATUS_NON_COMPLIANT &&
      status !== CONNECTION_STATUS_DISABLED
        ? (status as AuthenticationsStatus)
        : ("all" as AuthenticationsStatus),
    isRestricted,
    sharedWithAll: selectedShared === "everyone" || undefined,
    isManaged: selectedShared === "managed" ? true : undefined,
    isManagedAuthViolation:
      status === CONNECTION_STATUS_NON_COMPLIANT ||
      managedAuthViolation === "true"
        ? true
        : undefined,
  });

  /** Loading state  *********************************************************
   * only show loading when filters change */

  const [didFiltersChange, setDidFiltersChange] = useState(true);

  useEffect(() => {
    setDidFiltersChange(true);
  }, [
    search,
    order,
    owner,
    owners,
    ownersOperator,
    ownerStatus,
    shared,
    sharedByMe,
    status,
    page,
    app,
    apps,
    appsOperator,
  ]);

  useEffect(() => {
    if (!isFetching) {
      setDidFiltersChange(false);
    }
  }, [isFetching]);

  return {
    isFetching: isFetching && didFiltersChange,
    isLoading,
    isLoadingSecondary,
    totalPages,
    totalCount,
    isNonCompliantFilter: status === CONNECTION_STATUS_NON_COMPLIANT,
    sortChange,
    connections,
    page,
  };
}
